import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const DISMISS_KEY = 'eve_book_upsell_dismissed';
let busy = false;

/** Redo's pop-up marks everything else on the page inert, so never open ours on top of it. */
function redoOpen() {
  const h = document.getElementById('redo-spotlight-container');
  return !!(h && h.shadowRoot && h.shadowRoot.querySelector('.MuiDialog-root'));
}

/** @returns {boolean} */
function isDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function rememberDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch (e) {
    /* storage unavailable: fine */
  }
}

function cartUrl() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root + 'cart.js';
}

/**
 * @param {Element} root
 * @param {string} message
 */
function showError(root, message) {
  const el = root.querySelector('[data-book-upsell-error]');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
}

function openCartDrawer() {
  const drawer = /** @type {any} */ (document.querySelector('#cart-drawer'));
  if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
    drawer.open();
    return true;
  }
  return Boolean(drawer && drawer.hasAttribute('open'));
}

/**
 * Add the book through the same event flow the theme uses, so the drawer
 * morphs from the returned section HTML and the header count updates.
 * @param {HTMLElement} trigger
 */
async function addBook(trigger) {
  const root = /** @type {HTMLElement | null} */ (trigger.closest('[data-book-upsell]'));
  if (!root || busy) return;

  const variantId = root.dataset.variantId;
  if (!variantId) return;

  busy = true;
  trigger.setAttribute('aria-busy', 'true');
  if (trigger.getAttribute('role') === 'switch') trigger.setAttribute('aria-checked', 'true');
  const errorEl = root.querySelector('[data-book-upsell-error]');
  if (errorEl) /** @type {HTMLElement} */ (errorEl).hidden = true;

  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
  });

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  formData.append('sections', Array.from(sectionIds).join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  const fromPopup = root.tagName === 'DIALOG';

  trigger.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: fromPopup ? 'product' : 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const fail = (message, response) => {
    trigger.removeAttribute('aria-busy');
    if (trigger.getAttribute('role') === 'switch') trigger.setAttribute('aria-checked', 'false');
    showError(root, message);
    trigger.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: response && response.description, errors: response && response.errors },
      })
    );
  };

  try {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    }).then((r) => r.json());

    const ajaxCart = await fetch(cartUrl()).then((r) => r.json());

    if (response.status) {
      fail(response.message || 'Could not add the book. Please try again.', response);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: true,
          items: ajaxCart.items,
          source: 'book-upsell',
          sourceId: 'book-upsell',
          itemCount: 1,
          productId: root.dataset.productId,
        },
      });
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'book-upsell',
        sourceId: 'book-upsell',
        itemCount: 1,
        productId: root.dataset.productId,
        sections: response.sections,
        didError: false,
      },
    });

    if (fromPopup) {
      /** @type {HTMLDialogElement} */ (root).close();
      // The drawer defers its auto-open until a source modal closes; if auto-open is off, open it here.
      setTimeout(openCartDrawer, 60);
    }
  } catch (error) {
    console.error('[book-upsell]', error);
    deferred.reject(error);
    fail('Could not add the book. Please try again.', null);
  } finally {
    busy = false;
  }
}

/**
 * @param {Element} dialogEl
 * @param {boolean} dismiss
 */
function closePopup(dialogEl, dismiss) {
  if (dismiss) rememberDismissed();
  if (dialogEl instanceof HTMLDialogElement && dialogEl.open) dialogEl.close();
}

/** @param {string} addedTitle */
function showPopup(addedTitle) {
  const template = /** @type {HTMLTemplateElement | null} */ (document.querySelector('template[data-book-upsell-popup]'));
  if (!template || document.querySelector('dialog.book-upsell-popup') || redoOpen()) return;

  const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
  const dialog = fragment.querySelector('dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.querySelectorAll('[data-added-title]').forEach((el) => {
    el.textContent = addedTitle;
  });

  dialog.addEventListener('close', () => dialog.remove());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closePopup(dialog, true);
  });

  document.body.appendChild(dialog);
  dialog.showModal();
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const add = target.closest('[data-book-upsell-add]');
  if (add instanceof HTMLElement) {
    event.preventDefault();
    addBook(add);
    return;
  }

  const close = target.closest('[data-book-upsell-close]');
  if (close) {
    closePopup(/** @type {Element} */ (close.closest('dialog')), true);
    return;
  }

  const dismissBtn = target.closest('[data-book-upsell-dismiss]');
  if (dismissBtn) {
    closePopup(/** @type {Element} */ (dismissBtn.closest('dialog')), true);
    return;
  }

  const viewCart = target.closest('[data-book-upsell-viewcart]');
  if (viewCart) {
    const dialog = viewCart.closest('dialog');
    if (document.querySelector('#cart-drawer')) {
      event.preventDefault();
      closePopup(/** @type {Element} */ (dialog), false);
      setTimeout(() => {
        if (!openCartDrawer()) window.location.href = /** @type {HTMLAnchorElement} */ (viewCart).href;
      }, 60);
    }
  }
});

// Pop-up: only runs where the product page rendered the template.
document.addEventListener(StandardEvents.cartLinesUpdate, (/** @type {any} */ event) => {
  const template = /** @type {HTMLTemplateElement | null} */ (document.querySelector('template[data-book-upsell-popup]'));
  if (!template || event.action !== 'add' || isDismissed()) return;
  if (event.target instanceof Element && event.target.closest('[data-book-upsell]')) return;
  if (event.context === 'cart') return;

  const bookVariantId = Number(template.dataset.bookVariantId);
  const line = event.lines && event.lines[0];
  if (line && Number(line.merchandiseId) === bookVariantId) return;

  event.promise
    ?.then(({ detail }) => {
      if (!detail || detail.didError) return;
      const items = detail.items || [];
      if (items.some((item) => item.variant_id === bookVariantId)) return;
      const added = line ? items.find((item) => String(item.variant_id) === String(line.merchandiseId)) : null;
      showPopup(added ? added.product_title : 'Your item');
    })
    .catch(() => {});
});

// Reverse flow: book is the only thing in the cart -> offer matching pajamas.
const PJ_KEY = 'eve_pajama_upsell_dismissed';
const PJ_OFFER = '$5 off';
const PJ_URL = '/discount/BOOKPJ5?redirect=/collections/all-pajamas';

function showPajamaPopup() {
  try { if (sessionStorage.getItem(PJ_KEY) === '1') return; } catch (e) {}
  const t = document.querySelector('template[data-pajama-upsell-popup]');
  if (!t || document.querySelector('dialog.book-upsell-popup') || redoOpen()) return;
  const d = t.content.cloneNode(true).querySelector('dialog');
  if (!d) return;
  const done = () => { try { sessionStorage.setItem(PJ_KEY, '1'); } catch (e) {} d.close(); };
  d.addEventListener('close', () => d.remove());
  d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-pj-close]')) done(); });
  document.body.appendChild(d);
  d.showModal();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (/** @type {any} */ event) => {
  const template = document.querySelector('template[data-book-upsell-popup]');
  if (!template || event.action !== 'add') return;
  const bookId = Number(template.dataset.bookVariantId);
  event.promise?.then(({ detail }) => {
    if (!detail || detail.didError) return;
    const items = detail.items || [];
    if (items.length && items.every((i) => Number(i.variant_id) === bookId)) setTimeout(showPajamaPopup, 400);
  }).catch(() => {});
});